import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { FileText, Shield, CreditCard, Mail, AlertTriangle, Scale } from 'lucide-react';

export default function TermsConditions() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const content = {
    en: {
      title: "Terms and Conditions",
      lastUpdated: "Last updated: February 2025",
      sections: [
        {
          icon: FileText,
          title: "1. General",
          content: `These Terms and Conditions apply to all purchases and use of the Ella Gold Official platform ("the Platform"). By accessing or using our Platform, you agree to be bound by these Terms. If you do not agree with any part of these terms, you may not use our services.

The Platform is operated by Ella Gold, registered with the Dutch Chamber of Commerce (KVK: 99681293), located at Torenallee 101, 5617BR Eindhoven, The Netherlands. Our services include the sale of digital art, tokens, subscriptions, and AI-generated content.`
        },
        {
          icon: Shield,
          title: "2. Age Requirement",
          content: `You must be at least 18 years of age to use this Platform. By using our services, you confirm that you are 18 years or older. We reserve the right to request proof of age at any time and to terminate accounts that violate this requirement.

We implement age verification measures to protect minors. Providing false information about your age is strictly prohibited and may result in immediate account termination.`
        },
        {
          icon: CreditCard,
          title: "3. Purchases & Payments",
          content: `All prices on the Platform are displayed in Euros (€) and include 21% Dutch VAT where applicable. Payments are processed securely through Stripe, which supports iDEAL, credit cards, and other payment methods.

Upon successful payment:
• You will receive an order confirmation email
• Digital products are delivered instantly to your account
• An invoice/receipt will be available in your dashboard

Token purchases are non-refundable once tokens have been used. Subscription cancellations can be made at any time and will take effect at the end of the current billing period.`
        },
        {
          icon: Scale,
          title: "4. Refund Policy",
          content: `Due to the digital nature of our products, refunds are limited:

• Unused token packages: Full refund within 14 days of purchase
• Partially used tokens: No refund available
• Subscriptions: Cancel anytime, access continues until period ends
• Custom AI artwork: No refunds once generation has started
• Digital downloads: No refunds once downloaded

For technical issues preventing access to purchased content, please contact our support within 48 hours for assistance.`
        },
        {
          icon: FileText,
          title: "5. Intellectual Property",
          content: `All content on this Platform, including but not limited to images, videos, text, graphics, and AI-generated artwork, is protected by copyright and other intellectual property rights.

Purchased content grants you a personal, non-transferable license to view and download for personal use. You may NOT:
• Redistribute, resell, or share purchased content
• Use content for commercial purposes without explicit permission
• Claim ownership or authorship of any Platform content
• Use content to train AI models or machine learning systems`
        },
        {
          icon: AlertTriangle,
          title: "6. Prohibited Use",
          content: `You agree not to:
• Share account credentials or purchased content with others
• Attempt to bypass payment systems or security measures
• Upload malicious content or attempt to hack the Platform
• Use automated systems to access or scrape content
• Harass other users or Platform staff
• Violate any applicable local, national, or international laws

Violation of these terms may result in immediate account termination without refund.`
        },
        {
          icon: Shield,
          title: "7. Limitation of Liability",
          content: `The Platform is provided "as is" without warranties of any kind. We are not liable for:
• Temporary service interruptions or technical issues
• Loss of data due to circumstances beyond our control
• Third-party payment processor issues
• Content generated by AI systems

Our total liability shall not exceed the amount paid by you in the 12 months preceding any claim.`
        },
        {
          icon: Scale,
          title: "8. Governing Law",
          content: `These Terms are governed by Dutch law. Any disputes will be resolved in the competent courts of the Netherlands. The European Online Dispute Resolution platform is available at ec.europa.eu/consumers/odr for alternative dispute resolution.`
        }
      ],
      contact: "For questions about these Terms, contact us at:"
    },
    nl: {
      title: "Algemene Voorwaarden",
      lastUpdated: "Laatst bijgewerkt: Februari 2025",
      sections: [
        {
          icon: FileText,
          title: "1. Algemeen",
          content: `Deze Algemene Voorwaarden zijn van toepassing op alle aankopen en gebruik van het Ella Gold Official platform ("het Platform"). Door toegang tot of gebruik van ons Platform, gaat u akkoord met deze Voorwaarden. Als u niet akkoord gaat met enig deel van deze voorwaarden, mag u onze diensten niet gebruiken.

Het Platform wordt beheerd door Ella Gold, ingeschreven bij de Kamer van Koophandel (KVK: 99681293), gevestigd te Torenallee 101, 5617BR Eindhoven, Nederland. Onze diensten omvatten de verkoop van digitale kunst, tokens, abonnementen en AI-gegenereerde content.`
        },
        {
          icon: Shield,
          title: "2. Leeftijdsvereiste",
          content: `U moet minimaal 18 jaar oud zijn om dit Platform te gebruiken. Door gebruik te maken van onze diensten, bevestigt u dat u 18 jaar of ouder bent. Wij behouden ons het recht voor om op elk moment een leeftijdsbewijs te vragen en accounts te beëindigen die deze vereiste schenden.

Wij implementeren leeftijdsverificatiemaatregelen om minderjarigen te beschermen. Het verstrekken van valse informatie over uw leeftijd is strikt verboden en kan leiden tot onmiddellijke accountbeëindiging.`
        },
        {
          icon: CreditCard,
          title: "3. Aankopen & Betalingen",
          content: `Alle prijzen op het Platform worden weergegeven in Euro's (€) en zijn inclusief 21% Nederlandse BTW waar van toepassing. Betalingen worden veilig verwerkt via Stripe, dat iDEAL, creditcards en andere betaalmethoden ondersteunt.

Na succesvolle betaling:
• Ontvangt u een orderbevestiging per e-mail
• Worden digitale producten direct geleverd aan uw account
• Is een factuur/bon beschikbaar in uw dashboard

Token-aankopen zijn niet-restitueerbaar zodra tokens zijn gebruikt. Abonnementsopzeggingen kunnen op elk moment worden gedaan en gaan in aan het einde van de huidige factureringsperiode.`
        },
        {
          icon: Scale,
          title: "4. Retourbeleid",
          content: `Vanwege de digitale aard van onze producten zijn restituties beperkt:

• Ongebruikte tokenpakketten: Volledige restitutie binnen 14 dagen na aankoop
• Gedeeltelijk gebruikte tokens: Geen restitutie beschikbaar
• Abonnementen: Op elk moment opzeggen, toegang blijft tot einde periode
• Aangepaste AI-kunstwerken: Geen restitutie zodra generatie is gestart
• Digitale downloads: Geen restitutie na downloaden

Voor technische problemen die toegang tot gekochte content verhinderen, neem binnen 48 uur contact op met onze support.`
        },
        {
          icon: FileText,
          title: "5. Intellectueel Eigendom",
          content: `Alle content op dit Platform, inclusief maar niet beperkt tot afbeeldingen, video's, tekst, graphics en AI-gegenereerde kunstwerken, is beschermd door auteursrecht en andere intellectuele eigendomsrechten.

Gekochte content verleent u een persoonlijke, niet-overdraagbare licentie om te bekijken en te downloaden voor persoonlijk gebruik. U mag NIET:
• Gekochte content herverdelen, doorverkopen of delen
• Content gebruiken voor commerciële doeleinden zonder expliciete toestemming
• Eigendom of auteurschap van Platform-content claimen
• Content gebruiken om AI-modellen of machine learning-systemen te trainen`
        },
        {
          icon: AlertTriangle,
          title: "6. Verboden Gebruik",
          content: `U stemt ermee in om niet:
• Accountgegevens of gekochte content met anderen te delen
• Te proberen betalingssystemen of beveiligingsmaatregelen te omzeilen
• Schadelijke content te uploaden of te proberen het Platform te hacken
• Geautomatiseerde systemen te gebruiken om content te openen of te scrapen
• Andere gebruikers of Platform-medewerkers te intimideren
• Toepasselijke lokale, nationale of internationale wetten te schenden

Schending van deze voorwaarden kan leiden tot onmiddellijke accountbeëindiging zonder restitutie.`
        },
        {
          icon: Shield,
          title: "7. Beperking van Aansprakelijkheid",
          content: `Het Platform wordt geleverd "zoals het is" zonder garanties van welke aard dan ook. Wij zijn niet aansprakelijk voor:
• Tijdelijke serviceonderbrekingen of technische problemen
• Verlies van gegevens door omstandigheden buiten onze controle
• Problemen met externe betalingsverwerkers
• Content gegenereerd door AI-systemen

Onze totale aansprakelijkheid zal niet hoger zijn dan het bedrag dat u in de 12 maanden voorafgaand aan een claim heeft betaald.`
        },
        {
          icon: Scale,
          title: "8. Toepasselijk Recht",
          content: `Deze Voorwaarden worden beheerst door Nederlands recht. Geschillen worden beslecht door de bevoegde rechtbanken in Nederland. Het Europese Online Geschillenbeslechting platform is beschikbaar op ec.europa.eu/consumers/odr voor alternatieve geschillenbeslechting.`
        }
      ],
      contact: "Voor vragen over deze Voorwaarden, neem contact op via:"
    },
    de: {
      title: "Allgemeine Geschäftsbedingungen",
      lastUpdated: "Zuletzt aktualisiert: Januar 2025",
      sections: [
        {
          icon: FileText,
          title: "1. Allgemeines",
          content: `Diese Allgemeinen Geschäftsbedingungen gelten für alle Käufe und die Nutzung der Ella Gold Official Plattform ("die Plattform"). Durch den Zugriff auf oder die Nutzung unserer Plattform stimmen Sie diesen Bedingungen zu. Wenn Sie mit einem Teil dieser Bedingungen nicht einverstanden sind, dürfen Sie unsere Dienste nicht nutzen.`
        },
        {
          icon: Shield,
          title: "2. Altersanforderung",
          content: `Sie müssen mindestens 18 Jahre alt sein, um diese Plattform zu nutzen. Durch die Nutzung unserer Dienste bestätigen Sie, dass Sie 18 Jahre oder älter sind.`
        },
        {
          icon: CreditCard,
          title: "3. Käufe & Zahlungen",
          content: `Alle Preise auf der Plattform werden in Euro (€) angezeigt und enthalten 21% niederländische MwSt. Zahlungen werden sicher über Stripe verarbeitet.`
        },
        {
          icon: Scale,
          title: "4. Rückerstattungsrichtlinie",
          content: `Aufgrund der digitalen Natur unserer Produkte sind Rückerstattungen begrenzt. Ungenutzte Token-Pakete: Volle Rückerstattung innerhalb von 14 Tagen.`
        }
      ],
      contact: "Bei Fragen zu diesen Bedingungen kontaktieren Sie uns unter:"
    },
    fr: {
      title: "Conditions Générales",
      lastUpdated: "Dernière mise à jour: Janvier 2025",
      sections: [
        {
          icon: FileText,
          title: "1. Général",
          content: `Ces Conditions Générales s'appliquent à tous les achats et à l'utilisation de la plateforme Ella Gold Official ("la Plateforme"). En accédant ou en utilisant notre Plateforme, vous acceptez ces Conditions.`
        },
        {
          icon: Shield,
          title: "2. Condition d'Âge",
          content: `Vous devez avoir au moins 18 ans pour utiliser cette Plateforme. En utilisant nos services, vous confirmez avoir 18 ans ou plus.`
        },
        {
          icon: CreditCard,
          title: "3. Achats & Paiements",
          content: `Tous les prix sur la Plateforme sont affichés en Euros (€) et incluent 21% de TVA néerlandaise. Les paiements sont traités de manière sécurisée via Stripe.`
        },
        {
          icon: Scale,
          title: "4. Politique de Remboursement",
          content: `En raison de la nature numérique de nos produits, les remboursements sont limités.`
        }
      ],
      contact: "Pour toute question concernant ces Conditions, contactez-nous à:"
    },
    es: {
      title: "Términos y Condiciones",
      lastUpdated: "Última actualización: Enero 2025",
      sections: [
        {
          icon: FileText,
          title: "1. General",
          content: `Estos Términos y Condiciones se aplican a todas las compras y uso de la plataforma Ella Gold Official ("la Plataforma"). Al acceder o usar nuestra Plataforma, acepta estos Términos.`
        },
        {
          icon: Shield,
          title: "2. Requisito de Edad",
          content: `Debe tener al menos 18 años para usar esta Plataforma. Al usar nuestros servicios, confirma que tiene 18 años o más.`
        }
      ],
      contact: "Para preguntas sobre estos Términos, contáctenos en:"
    },
    it: {
      title: "Termini e Condizioni",
      lastUpdated: "Ultimo aggiornamento: Gennaio 2025",
      sections: [
        {
          icon: FileText,
          title: "1. Generale",
          content: `Questi Termini e Condizioni si applicano a tutti gli acquisti e all'uso della piattaforma Ella Gold Official ("la Piattaforma"). Accedendo o utilizzando la nostra Piattaforma, accetti questi Termini.`
        },
        {
          icon: Shield,
          title: "2. Requisito di Età",
          content: `Devi avere almeno 18 anni per utilizzare questa Piattaforma. Utilizzando i nostri servizi, confermi di avere 18 anni o più.`
        }
      ],
      contact: "Per domande su questi Termini, contattaci a:"
    },
    ru: {
      title: "Условия использования",
      lastUpdated: "Последнее обновление: Январь 2025",
      sections: [
        {
          icon: FileText,
          title: "1. Общие положения",
          content: `Настоящие Условия использования применяются ко всем покупкам и использованию платформы Ella Gold Official ("Платформа"). Получая доступ к нашей Платформе или используя её, вы соглашаетесь с этими Условиями.`
        },
        {
          icon: Shield,
          title: "2. Возрастные требования",
          content: `Вам должно быть не менее 18 лет для использования этой Платформы. Используя наши услуги, вы подтверждаете, что вам 18 лет или больше.`
        }
      ],
      contact: "По вопросам об этих Условиях свяжитесь с нами:"
    },
    ar: {
      title: "الشروط والأحكام",
      lastUpdated: "آخر تحديث: يناير 2025",
      sections: [
        {
          icon: FileText,
          title: "1. عام",
          content: `تنطبق هذه الشروط والأحكام على جميع المشتريات واستخدام منصة Ella Gold Official ("المنصة"). من خلال الوصول إلى منصتنا أو استخدامها، فإنك توافق على هذه الشروط.`
        },
        {
          icon: Shield,
          title: "2. متطلبات العمر",
          content: `يجب أن يكون عمرك 18 عامًا على الأقل لاستخدام هذه المنصة. باستخدام خدماتنا، تؤكد أن عمرك 18 عامًا أو أكثر.`
        }
      ],
      contact: "للأسئلة حول هذه الشروط، اتصل بنا على:"
    }
  };

  const currentContent = content[lang] || content.en;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="terms-page">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
            <Scale className="w-10 h-10 text-[#D4AF37]" />
            {currentContent.title}
          </h1>
          <p className="text-gray-400">{currentContent.lastUpdated}</p>
        </div>

        <div className="space-y-8">
          {currentContent.sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <div 
                key={idx}
                className="p-6 bg-white/5 rounded-xl border border-white/10"
              >
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-3">
                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                  {section.title}
                </h2>
                <div className="text-gray-300 whitespace-pre-line leading-relaxed">
                  {section.content}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 p-6 bg-[#D4AF37]/10 rounded-xl border border-[#D4AF37]/30 text-center">
          <p className="text-gray-300 mb-2">{currentContent.contact}</p>
          <a href="mailto:support@ellagold.com" className="text-[#D4AF37] hover:underline font-medium">
            support@ellagold.com
          </a>
        </div>
      </div>
    </div>
  );
}
